import clsx from 'clsx'

// Components
import { type TooltipProps } from '@/components/ui/tooltip/Tooltip'
import TruncatedText from '@/components/truncated-text/TruncatedText'

export type BreadcrumbItemType = {
  /**
   * A unique ID for the breadcrumb item
   */
  id: string
  /**
   * The label of the breadcrumb item
   */
  label: string
  /**
   * The onClick handler for the breadcrumb item
   */
  onClick?: () => void
}

export type BreadcrumbTextSize = 'sm' | 'base'

type BreadcrumbItemProps = {
  /**
   * The item to display in the breadcrumb
   */
  item: BreadcrumbItemType
  /**
   * Whether the item is active i.e. the current page
   */
  isActive?: boolean
  /**
   * The maximum character length of the label to display (non-active segments).
   */
  maxLabelLength: number

  /**
   * Maximum character length of the active label. Omit to let the label fill the available
   * width and clip with a CSS ellipsis.
   */
  maxActiveLabelLength?: number

  /**
   * Text size variant for breadcrumb labels.
   */
  textSize?: BreadcrumbTextSize

  /**
   * Placement of the full-label tooltip shown on a truncated label.
   */
  tooltipPlacement?: TooltipProps['placement']
}

const truncateLabel = (label: string, maxLabelLength: number): string => {
  if (label.length <= maxLabelLength) {
    return label
  }
  return `${label.slice(0, maxLabelLength)}...`
}

export function BreadcrumbItem({
  item,
  isActive = false,
  maxLabelLength,
  maxActiveLabelLength,
  textSize = 'sm',
  tooltipPlacement = 'top-start',
}: BreadcrumbItemProps) {
  const maxLength = isActive ? maxActiveLabelLength : maxLabelLength
  const displayLabel = maxLength === undefined ? item.label : truncateLabel(item.label, maxLength)

  // The full label; a length-capped `displayLabel` shows it in the tooltip even though the capped text fits.
  const tooltipProps = { title: item.label, placement: tooltipPlacement, offsetX: 2 }

  // Active items are non-interactive by default, but an onClick overrides that
  // e.g. when a navigable item is the only breadcrumb and therefore the active item (last item)
  const content =
    isActive && !item.onClick ? (
      <TruncatedText
        {...tooltipProps}
        className={clsx('w-full font-semibold text-text-primary cursor-default px-2', textSize === 'base' ? 'text-base py-0' : 'text-sm py-0.5')}
      >
        {displayLabel}
      </TruncatedText>
    ) : (
      <TruncatedText
        {...tooltipProps}
        as="button"
        onClick={item.onClick}
        className={clsx(
          'max-w-full text-left font-semibold text-text-tertiary cursor-pointer rounded-md px-2 hover:bg-surface-hover hover:text-text-tertiary active:bg-surface-hover active:text-text-primary focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
          textSize === 'base' ? 'text-base py-0' : 'text-sm py-0.5'
        )}
      >
        {displayLabel}
      </TruncatedText>
    )

  return <span className="inline-flex min-w-0 w-full max-w-full items-center">{content}</span>
}
